/**
 * Feed Resource
 *
 * Thin 1:1 mapping to `/v1/feed`. The acting user and tenant come from the JWT
 * (claims account). `connect()` returns a LiveFeed handle that owns the home
 * window (feed ADR-008).
 */

import type { HttpLayer } from '../http.js';
import type { FeedCache } from '../cache/feed-cache.js';
import { LiveFeed } from '../feed/live-feed.js';
import type {
  EngageFeedPostRequest,
  FeedPost,
  FeedSubscribeRequest,
  FeedThread,
  ListFeedHomeRequest,
  ListFeedHomeResponse,
  MarkFeedSeenResponse,
  PublishFeedPostRequest,
} from '../types/feed.js';

export class FeedResource {
  constructor(
    private http: HttpLayer,
    private feedCache: FeedCache,
  ) {}

  /** Publish an original, reply, quote, or repost. Poster is the authenticated user. */
  async publish(params: PublishFeedPostRequest): Promise<FeedPost> {
    const post = await this.http.post<FeedPost>('/v1/feed/posts', params);
    // Own-mutation invalidation at the mutation point.
    this.feedCache.evictAll();
    return post;
  }

  /**
   * One bidirectional keyset page of the account home timeline. Cache consult:
   * a stored page window
   * serves instantly and revalidates behind the return.
   */
  async listHome(params: ListFeedHomeRequest = {}): Promise<ListFeedHomeResponse> {
    const identity = await this.http.actingAccountId();
    const key = feedHomeKey(params);
    const cached = this.feedCache.lookup(identity, key);
    if (cached) {
      void this.revalidateHome(identity, params).catch(() => {
        // no-fallbacks.md exception: quiet degradation — the stored
        // window stays readable; the next read retries and reconnect heals.
      });
      return cached;
    }
    const fetched = await this.http.post<ListFeedHomeResponse>('/v1/feed/home', params);
    this.feedCache.store(identity, key, fetched);
    return fetched;
  }

  private async revalidateHome(
    identity: string,
    params: ListFeedHomeRequest,
  ): Promise<void> {
    const fresh = await this.http.post<ListFeedHomeResponse>('/v1/feed/home', params);
    this.feedCache.store(identity, feedHomeKey(params), fresh);
  }

  /** Hydrate one post by permalink id. */
  async getPost(postId: string): Promise<FeedPost> {
    return this.http.get(`/v1/feed/posts/${postId}`);
  }

  /** One-shot thread: root, ancestors, focused post, latest 20 replies. */
  async getThread(postId: string): Promise<FeedThread> {
    return this.http.get(`/v1/feed/posts/${postId}/thread`);
  }

  /** Toggle like, bookmark, or repost on a post. */
  async engage(postId: string, params: EngageFeedPostRequest): Promise<FeedPost> {
    const post = await this.http.post<FeedPost>(`/v1/feed/posts/${postId}/engage`, params);
    this.feedCache.evictAll();
    return post;
  }

  /** Advance the visit watermark; returns remaining unseen home count. */
  async markSeen(): Promise<MarkFeedSeenResponse> {
    return this.http.post('/v1/feed/seen', {});
  }

  /** Soft-delete. Author / account owner / org take-down. */
  async delete(postId: string): Promise<FeedPost> {
    const post = await this.http.delete<FeedPost>(`/v1/feed/posts/${postId}`);
    this.feedCache.evictAll();
    return post;
  }

  /** Kick Feed.Replay to this device after the optional cursor. */
  async subscribe(params: FeedSubscribeRequest): Promise<{ status: string }> {
    return this.http.post('/v1/feed/subscribe', params);
  }

  /** Drop replay interest for this device. */
  async unsubscribe(deviceId: string): Promise<{ status: string }> {
    return this.http.post('/v1/feed/unsubscribe', { device_id: deviceId });
  }

  /**
   * Open a LiveFeed handle: bounded home window, loadOlder/loadNewer, publish
   * apply, unseen watermark, and subscribe/replay owned by the host that calls
   * `subscribe` after connect (feed ADR-008).
   */
  async connect(): Promise<LiveFeed> {
    const handle = new LiveFeed(this, this.feedCache);
    await handle.hydrate();
    return handle;
  }
}

export function feedHomeKey(params: ListFeedHomeRequest): string {
  return `feed.home|${JSON.stringify(params)}`;
}
